import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowDown, ArrowLeft, ArrowUp, Check, ChevronDown, GitCompareArrows, Radar, Table2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type {
  Evaluation,
  EvaluationCategory,
  EvaluationCriterion,
  EvaluationScore,
  EvaluationStatus,
  EvaluationTemplate,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { EditableDate, EditableTitle, ErrorNote, Loading, Page } from '@/components/page'
import { ScaleLegend, scaleLabelFor } from '@/components/scale-legend'
import { RadarChart } from '@/components/radar-chart'
import { NoteField, ScoreButtons, answerLabel, unitLabel } from '@/components/criterion-scoring'

interface Detail {
  evaluation: Evaluation
  template: EvaluationTemplate
  categories: EvaluationCategory[]
  criteria: EvaluationCriterion[]
  scores: EvaluationScore[]
  /** Other runs of the same template, for the comparison picker. */
  siblings: Evaluation[]
}

export function EvaluationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isLead } = useAuth()
  const [compareId, setCompareId] = React.useState<string>('none')
  const [compareScores, setCompareScores] = React.useState<EvaluationScore[]>([])

  const { data, loading, error, refetch, setData } = useQuery<Detail | null>(async () => {
    if (!id) return null
    const { data: ev, error: eerr } = await sb().from('evaluations').select('*').eq('id', id).maybeSingle()
    if (eerr) throw eerr
    if (!ev) return null
    const evaluation = ev as Evaluation

    const [tpl, cats, scores] = await Promise.all([
      sb().from('evaluation_templates').select('*').eq('id', evaluation.template_id).single(),
      sb().from('evaluation_categories').select('*').eq('template_id', evaluation.template_id).order('display_order'),
      sb().from('evaluation_scores').select('*').eq('evaluation_id', evaluation.id),
    ])
    if (tpl.error) throw tpl.error
    if (cats.error) throw cats.error
    if (scores.error) throw scores.error

    const categories = (cats.data ?? []) as EvaluationCategory[]
    let criteria: EvaluationCriterion[] = []
    if (categories.length > 0) {
      const { data: cr, error: crerr } = await sb()
        .from('evaluation_criteria')
        .select('*')
        .in('category_id', categories.map((c) => c.id))
        .order('display_order')
      if (crerr) throw crerr
      criteria = (cr ?? []) as EvaluationCriterion[]
    }

    const { data: sib, error: serr } = await sb()
      .from('evaluations')
      .select('*')
      .eq('team_id', evaluation.team_id)
      .eq('template_id', evaluation.template_id)
      .neq('id', evaluation.id)
      .order('conducted_on', { ascending: false })
    if (serr) throw serr

    return {
      evaluation,
      template: tpl.data as EvaluationTemplate,
      categories,
      criteria,
      scores: (scores.data ?? []) as EvaluationScore[],
      siblings: (sib ?? []) as Evaluation[],
    }
  }, [id])

  // Default the comparison to the most recent earlier run, since "how did we
  // move" is the question this page exists to answer.
  React.useEffect(() => {
    if (!data || compareId !== 'none') return
    const earlier = data.siblings.find((s) => s.conducted_on <= data.evaluation.conducted_on)
    if (earlier) setCompareId(earlier.id)
  }, [data?.evaluation.id]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (compareId === 'none') {
      setCompareScores([])
      return
    }
    let active = true
    void sb()
      .from('evaluation_scores')
      .select('*')
      .eq('evaluation_id', compareId)
      .then(({ data: s }) => {
        if (active) setCompareScores((s ?? []) as EvaluationScore[])
      })
    return () => {
      active = false
    }
  }, [compareId])

  const scoreByCriterion = React.useMemo(() => {
    const m = new Map<string, EvaluationScore>()
    for (const s of data?.scores ?? []) m.set(s.criterion_id, s)
    return m
  }, [data?.scores])

  const compareByCriterion = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const s of compareScores) m.set(s.criterion_id, Number(s.score))
    return m
  }, [compareScores])

  /**
   * Write a score. Upsert on (evaluation_id, criterion_id) so tapping a
   * different number corrects the row rather than adding a second one, and
   * patch local state immediately — scoring 72 criteria in a room full of
   * people should never wait on a round trip.
   */
  async function setScore(criterion: EvaluationCriterion, score: number, notes?: string) {
    if (!data) return
    const existing = scoreByCriterion.get(criterion.id)
    const optimistic: EvaluationScore = {
      id: existing?.id ?? `pending-${criterion.id}`,
      evaluation_id: data.evaluation.id,
      criterion_id: criterion.id,
      team_id: data.evaluation.team_id,
      score,
      notes: notes ?? existing?.notes ?? null,
      scored_by: existing?.scored_by ?? null,
    }
    setData((prev) =>
      prev
        ? {
            ...prev,
            scores: [...prev.scores.filter((s) => s.criterion_id !== criterion.id), optimistic],
          }
        : prev,
    )

    const { error: err } = await sb().from('evaluation_scores').upsert(
      {
        evaluation_id: data.evaluation.id,
        criterion_id: criterion.id,
        team_id: data.evaluation.team_id,
        score,
        notes: optimistic.notes,
      },
      { onConflict: 'evaluation_id,criterion_id' },
    )
    if (err) {
      window.alert(err.message)
      void refetch()
    }
  }

  async function setStatus(status: EvaluationStatus) {
    if (!data) return
    const { error: err } = await sb().from('evaluations').update({ status }).eq('id', data.evaluation.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  /**
   * Rename, and re-date.
   *
   * `conducted_on` is not decoration: the Evaluations list sorts by it, the
   * comparison picker reads "the run before this one" off it, and the movement
   * chart plots against it. So this refetches instead of patching state — the
   * siblings and the chosen comparison have to be re-derived, and a run that
   * moves past another one changes what it is being compared with.
   *
   * Both fields are open to anyone who can write the team, matching the status
   * select beside them rather than the lead-only Delete: fixing a name or a
   * date is a correction, and the person who ran the session is usually the one
   * who notices. RLS (`evaluations_write`, 0004) is what actually decides.
   */
  async function patchEvaluation(patch: Partial<Pick<Evaluation, 'name' | 'conducted_on'>>) {
    if (!data) return
    const { error: err } = await sb().from('evaluations').update(patch).eq('id', data.evaluation.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  async function remove() {
    if (!data) return
    if (!window.confirm(`Delete “${data.evaluation.name}”?`)) return
    const { error: err } = await sb().from('evaluations').delete().eq('id', data.evaluation.id)
    if (err) return window.alert(err.message)
    navigate('/evaluations')
  }

  if (loading && !data) {
    return (
      <Page title="Evaluation">
        <Loading />
      </Page>
    )
  }
  if (error) {
    return (
      <Page title="Evaluation">
        <ErrorNote error={error} />
      </Page>
    )
  }
  if (!data) {
    return (
      <Page title="Evaluation not found">
        <Button variant="outline" asChild>
          <Link to="/evaluations">
            <ArrowLeft /> Back
          </Link>
        </Button>
      </Page>
    )
  }

  const { evaluation, template, categories, criteria } = data
  const scored = data.scores.length
  const total = criteria.length
  const pct = total === 0 ? 0 : Math.round((scored / total) * 100)
  const average =
    scored > 0 ? data.scores.reduce((a, s) => a + Number(s.score), 0) / scored : null

  const comparison = data.siblings.find((s) => s.id === compareId) ?? null

  /**
   * How the page is laid out, and nothing else — the data, the scoring and the
   * charts are identical either way.
   *
   * The Process assessment (0022) is run the way a Tech Stack review is: a
   * facilitator reads the prompts aloud and the room answers in order, once,
   * top to bottom. So it gets that page's shape — one scroll, every section
   * open, progress and comparison in a single strip, what moved stated up front
   * — and the charts sit underneath as the readout rather than the entry point.
   *
   * SWEBOK keeps the chart-first tabs: at 38 criteria across 10 areas it is
   * read far more often than it is scored, and the dumbbell is what it is read
   * for. Keyed on slug because that difference is editorial — nothing in the
   * schema says how a template is meant to be run.
   */
  const conversational = template.slug === 'process'

  const categoryStats = categories.map((cat) => {
    const own = criteria.filter((c) => c.category_id === cat.id)
    const now = own.map((c) => scoreByCriterion.get(c.id)).filter(Boolean).map((s) => Number(s!.score))
    const before = own.map((c) => compareByCriterion.get(c.id)).filter((v): v is number => v !== undefined)
    return {
      category: cat,
      criteria: own,
      now: now.length ? now.reduce((a, b) => a + b, 0) / now.length : null,
      before: before.length ? before.reduce((a, b) => a + b, 0) / before.length : null,
      scoredCount: now.length,
    }
  })

  // What moved since the run being compared against — this template's answer to
  // the Tech Stack review's "what joined the stack, what left". Only an area the
  // earlier run actually scored can move, and under a tenth of a point on a 1–5
  // scale is noise rather than movement.
  const moved = categoryStats
    .filter((s) => s.now != null && s.before != null && Math.abs(s.now - s.before) >= 0.05)
    .map((s) => ({ name: s.category.name, delta: s.now! - s.before! }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))

  // Averaged over whatever each run scored, so a half-finished run reads as the
  // average of what it answered rather than being dragged toward zero.
  const beforeAverage =
    compareScores.length > 0
      ? compareScores.reduce((a, s) => a + Number(s.score), 0) / compareScores.length
      : null
  const overallDelta = average != null && beforeAverage != null ? average - beforeAverage : null

  const scoringSections = (
    <div className="space-y-3">
      {categoryStats.map(({ category, criteria: own, scoredCount }) => (
        <CategoryScorer
          key={category.id}
          category={category}
          criteria={own}
          template={template}
          scoreByCriterion={scoreByCriterion}
          compareByCriterion={compareByCriterion}
          scoredCount={scoredCount}
          onScore={setScore}
          collapsible={!conversational}
        />
      ))}
    </div>
  )

  // The three read-only views, shared by both layouts: entry point for SWEBOK,
  // readout underneath the questions for Process. `extra` is where SWEBOK slots
  // its "Score it" tab, which Process doesn't have — its scoring is the page.
  const readoutTriggers = (extra?: React.ReactNode) => (
    <>
      <TabsTrigger value="chart">
        <GitCompareArrows className="mr-1.5 h-3.5 w-3.5" />
        By area
      </TabsTrigger>
      {/* The whole assessment in one shape. Deliberately never the default tab:
          the dumbbell is the better read for "which areas moved", and a radar's
          outline depends on the arbitrary order of its axes. See radar-chart.tsx. */}
      <TabsTrigger value="radar">
        <Radar className="mr-1.5 h-3.5 w-3.5" />
        Radar
      </TabsTrigger>
      {extra}
      <TabsTrigger value="table">
        <Table2 className="mr-1.5 h-3.5 w-3.5" />
        Table
      </TabsTrigger>
    </>
  )

  const readouts = (
    <>
      <TabsContent value="chart">
        <CategoryChart
          stats={categoryStats}
          template={template}
          comparison={comparison}
          evaluation={evaluation}
          conversational={conversational}
        />
      </TabsContent>

      <TabsContent value="radar">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Maturity across all {categoryStats.length} areas</CardTitle>
            <CardDescription>
              Scale {template.scale_min}–{template.scale_max}, {template.scale_min} at the centre
              {comparison ? ` · compared against ${comparison.name}` : ''}. Values are printed on
              each axis; the Table tab has the per-criterion detail.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadarChart
              axes={categoryStats.map((s) => ({
                label: s.category.name,
                now: s.now,
                before: s.before,
              }))}
              template={template}
              nowLabel={evaluation.name}
              beforeLabel={comparison?.name ?? null}
              className="mx-auto max-w-2xl"
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="table">
        <ScoreTable
          stats={categoryStats}
          template={template}
          scoreByCriterion={scoreByCriterion}
          compareByCriterion={compareByCriterion}
          comparison={comparison}
        />
      </TabsContent>
    </>
  )

  return (
    <Page
      title={
        <EditableTitle
          value={evaluation.name}
          label="Rename this evaluation"
          onRename={(name) => void patchEvaluation({ name })}
        />
      }
      description={
        <>
          {template.name}
          {template.version ? ` · ${template.version}` : ''} · conducted{' '}
          <EditableDate
            value={evaluation.conducted_on}
            display={formatDate(evaluation.conducted_on)}
            label="Change the date this evaluation was conducted"
            onChange={(conducted_on) => void patchEvaluation({ conducted_on })}
          />
        </>
      }
      actions={
        <>
          <Select value={evaluation.status} onValueChange={(v) => void setStatus(v as EvaluationStatus)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
          {isLead && (
            <Button variant="ghost" onClick={() => void remove()}>
              Delete
            </Button>
          )}
        </>
      }
    >
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" asChild>
        <Link to="/evaluations">
          <ArrowLeft /> Evaluations
        </Link>
      </Button>

      {conversational ? (
        /* Progress, where it stands, and what it is being compared against, in
           one strip — the same header the Tech Stack review uses, because the
           room needs all three at a glance and none of them a card apiece. */
        <Card className="mb-5">
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
            <div className="min-w-[200px] flex-1">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium">
                  {scored} of {total} scored
                </span>
                <span className="text-xs text-muted-foreground">{pct}%</span>
              </div>
              <Progress value={pct} className="mt-1.5 h-1.5" />
            </div>

            {average != null && (
              <div>
                <div className="text-2xl font-semibold tabular-nums leading-none">
                  {average.toFixed(1)}
                  <span className="text-sm font-normal text-muted-foreground"> / {template.scale_max}</span>
                </div>
                {scaleLabelFor(template, average) && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    ≈ {scaleLabelFor(template, average)}
                  </div>
                )}
              </div>
            )}

            {data.siblings.length > 0 && (
              <div className="flex items-center gap-2">
                <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
                <Select value={compareId} onValueChange={setCompareId}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Compare with…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No comparison</SelectItem>
                    {data.siblings.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {formatDate(s.conducted_on)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Average score</p>
            <p className="mt-1 text-3xl font-semibold tabular-nums">
              {average != null ? average.toFixed(1) : '—'}
              <span className="text-base font-normal text-muted-foreground"> / {template.scale_max}</span>
            </p>
            {average != null && scaleLabelFor(template, average) && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                ≈ {scaleLabelFor(template, average)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Progress</p>
            <p className="mt-1 text-3xl font-semibold tabular-nums">
              {scored}
              <span className="text-base font-normal text-muted-foreground"> / {total}</span>
            </p>
            <Progress value={pct} className="mt-2 h-1.5" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Compare against</p>
            {data.siblings.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">
                No other {template.name} runs yet. Run this again later to see movement.
              </p>
            ) : (
              <Select value={compareId} onValueChange={setCompareId}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nothing</SelectItem>
                  {data.siblings.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({formatDate(s.conducted_on)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Stated up front rather than left for the reader to find in a chart —
          the Tech Stack review's "Since <earlier review>" card, in the terms a
          scored template moves in. */}
      {conversational && comparison && moved.length > 0 && (
        <Card className="mb-5 border-primary/30 bg-primary/[0.03]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Since {comparison.name}</CardTitle>
            {overallDelta != null && (
              <CardDescription>
                Overall {beforeAverage!.toFixed(1)} → {average!.toFixed(1)}
                {Math.abs(overallDelta) >= 0.05 && (
                  <span className={cn('font-medium', overallDelta > 0 ? 'text-better' : 'text-worse')}>
                    {' '}
                    ({overallDelta > 0 ? '+' : ''}
                    {overallDelta.toFixed(1)})
                  </span>
                )}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="flex flex-wrap gap-1.5 pb-4">
            {moved.map((m) => (
              <Badge
                key={m.name}
                variant={m.delta > 0 ? 'success' : 'muted'}
                className={cn('gap-1 text-[11px]', m.delta < 0 && 'bg-worse/15 text-worse')}
              >
                {m.delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {m.name}
                <span className="tabular-nums">
                  {m.delta > 0 ? '+' : ''}
                  {m.delta.toFixed(1)}
                </span>
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="mb-5">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">
          Maturity scale
          {template.version && <span className="font-normal"> · {template.name} {template.version}</span>}
        </p>
        <ScaleLegend template={template} active={average} />
      </div>

      {conversational ? (
        <>
          {scoringSections}

          <div className="mt-8">
            <h2 className="mb-2 text-sm font-medium text-muted-foreground">Where this run landed</h2>
            <Tabs defaultValue="chart">
              <TabsList>{readoutTriggers()}</TabsList>
              {readouts}
            </Tabs>
          </div>
        </>
      ) : (
        <Tabs defaultValue="chart">
          <TabsList>{readoutTriggers(<TabsTrigger value="score">Score it</TabsTrigger>)}</TabsList>
          {readouts}
          <TabsContent value="score">{scoringSections}</TabsContent>
        </Tabs>
      )}
    </Page>
  )
}

type CategoryStat = {
  category: EvaluationCategory
  criteria: EvaluationCriterion[]
  now: number | null
  before: number | null
  scoredCount: number
}

/**
 * Before → after per knowledge area: a dumbbell.
 *
 * Built in CSS rather than a chart library because that's what a dumbbell is —
 * a track, two dots, and the segment between them — and doing it directly means
 * the marks inherit the theme's surface and text tokens instead of fighting a
 * library's SVG defaults. With no comparison selected it degrades to a single
 * marker per row, which is the right chart for "magnitude by category".
 */
function CategoryChart({
  stats,
  template,
  comparison,
  evaluation,
  conversational = false,
}: {
  stats: CategoryStat[]
  template: EvaluationTemplate
  comparison: Evaluation | null
  evaluation: Evaluation
  /** Where to send someone with nothing scored yet — a tab, or the page above. */
  conversational?: boolean
}) {
  const min = template.scale_min
  const max = template.scale_max
  const pos = (v: number) => ((v - min) / (max - min)) * 100

  const anyScored = stats.some((s) => s.now != null)
  if (!anyScored) {
    return (
      <Card className="border-dashed shadow-none">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          {conversational ? (
            <>Nothing scored yet. Work down the questions above and this fills in.</>
          ) : (
            <>
              Nothing scored yet. Switch to <span className="font-medium">Score it</span> to start.
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Average score by knowledge area</CardTitle>
        <CardDescription>
          Scale {min}–{max} · see the maturity scale above
        </CardDescription>
        {/* A legend is present whenever two series share the plot, so identity
            is never carried by color alone. */}
        {comparison && (
          <div className="flex flex-wrap items-center gap-4 pt-1 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: 'var(--chart-before)' }}
              />
              {comparison.name} <span className="text-muted-foreground">({formatDate(comparison.conducted_on)})</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: 'var(--chart-after)' }}
              />
              {evaluation.name} <span className="text-muted-foreground">({formatDate(evaluation.conducted_on)})</span>
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {stats.map(({ category, now, before }) => {
            const delta = now != null && before != null ? now - before : null
            return (
              <div
                key={category.id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-md px-1 py-2 sm:grid-cols-[220px_minmax(0,1fr)_auto]"
              >
                <div className="truncate text-sm font-medium sm:order-1" title={category.name}>
                  {category.name}
                </div>

                <div className="order-3 col-span-2 sm:order-2 sm:col-span-1">
                  <div className="relative h-6">
                    {/* Track */}
                    <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted" />

                    {now == null ? (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        not scored
                      </span>
                    ) : (
                      <>
                        {/* Segment between the two readings */}
                        {before != null && (
                          <div
                            className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full"
                            style={{
                              left: `${Math.min(pos(before), pos(now))}%`,
                              width: `${Math.abs(pos(now) - pos(before))}%`,
                              background: 'var(--chart-before)',
                              opacity: 0.55,
                            }}
                          />
                        )}
                        {before != null && (
                          <Dot value={before} left={pos(before)} color="var(--chart-before)" label={`${comparison?.name ?? 'before'}: ${before.toFixed(1)}`} />
                        )}
                        <Dot value={now} left={pos(now)} color="var(--chart-after)" label={`${evaluation.name}: ${now.toFixed(1)}`} />
                      </>
                    )}
                  </div>
                </div>

                <div className="order-2 flex items-baseline justify-end gap-2 sm:order-3">
                  <span className="text-sm font-semibold tabular-nums">
                    {now != null ? now.toFixed(1) : '—'}
                  </span>
                  {delta != null && Math.abs(delta) >= 0.05 && (
                    <span
                      className={cn(
                        'text-xs font-medium tabular-nums',
                        delta > 0 ? 'text-better' : 'text-worse',
                      )}
                    >
                      {delta > 0 ? '+' : ''}
                      {delta.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-3 flex justify-between border-t pt-2 text-[11px] text-muted-foreground">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function Dot({
  left,
  color,
  label,
}: {
  value: number
  left: number
  color: string
  label: string
}) {
  return (
    <span
      className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-background"
      style={{ left: `${left}%`, background: color }}
      title={label}
      aria-label={label}
    />
  )
}

function CategoryScorer({
  category,
  criteria,
  template,
  scoreByCriterion,
  compareByCriterion,
  scoredCount,
  onScore,
  /**
   * Collapsed sections suit a template you dip into. A template that is read
   * aloud in order wants every question on the page, the way a Tech Stack
   * review does — see `conversational` above.
   */
  collapsible = true,
}: {
  category: EvaluationCategory
  criteria: EvaluationCriterion[]
  template: EvaluationTemplate
  scoreByCriterion: Map<string, EvaluationScore>
  compareByCriterion: Map<string, number>
  scoredCount: number
  onScore: (c: EvaluationCriterion, score: number, notes?: string) => void
  collapsible?: boolean
}) {
  const [open, setOpen] = React.useState(!collapsible)
  const complete = scoredCount === criteria.length && criteria.length > 0

  const scale: number[] = []
  for (let v = template.scale_min; v <= template.scale_max; v++) scale.push(v)

  return (
    <Card>
      {collapsible ? (
        <button
          type="button"
          className="flex w-full items-center gap-3 p-4 text-left"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{category.name}</span>
              {complete && (
                <Badge variant="success" className="gap-1 text-[10px]">
                  <Check className="h-3 w-3" /> done
                </Badge>
              )}
            </div>
            {category.description && !open && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{category.description}</p>
            )}
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {scoredCount}/{criteria.length}
          </span>
        </button>
      ) : (
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">{category.name}</CardTitle>
            {complete && (
              <Badge variant="success" className="gap-1 text-[10px]">
                <Check className="h-3 w-3" /> done
              </Badge>
            )}
            <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
              {scoredCount}/{criteria.length}
            </span>
          </div>
          {category.description && (
            <p className="text-sm text-muted-foreground">{category.description}</p>
          )}
        </CardHeader>
      )}

      {open && (
        <CardContent className={cn(collapsible && 'border-t pt-4')}>
          {collapsible && category.description && (
            <p className="mb-4 text-sm text-muted-foreground">{category.description}</p>
          )}
          <div className="space-y-5">
            {criteria.map((c) => {
              const current = scoreByCriterion.get(c.id)
              const before = compareByCriterion.get(c.id)
              return (
                <div key={c.id}>
                  <p className="text-sm">{c.name}</p>
                  {c.prompt && <p className="mt-0.5 text-xs text-muted-foreground">{c.prompt}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <ScoreButtons
                      template={template}
                      criterion={c}
                      value={current != null ? Number(current.score) : null}
                      onPick={(v) => onScore(c, v)}
                    />
                    {/* Name the chosen score in words — the number alone doesn't
                        say what it means, which is the point of having a scale. */}
                    {current != null && answerLabel(template, c, Number(current.score)) && (
                      <span className="ml-1.5 text-xs font-medium">
                        {answerLabel(template, c, Number(current.score))}
                      </span>
                    )}
                    {before !== undefined && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        was {before}
                        {answerLabel(template, c, before) && ` (${answerLabel(template, c, before)})`}
                      </span>
                    )}
                  </div>
                  {current && (
                    <NoteField
                      key={`${c.id}-note`}
                      initial={current.notes ?? ''}
                      onCommit={(notes) => onScore(c, Number(current.score), notes)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

/** The table view the accessibility pass requires: every number, readable. */
function ScoreTable({
  stats,
  template,
  scoreByCriterion,
  compareByCriterion,
  comparison,
}: {
  stats: CategoryStat[]
  template: EvaluationTemplate
  scoreByCriterion: Map<string, EvaluationScore>
  compareByCriterion: Map<string, number>
  comparison: Evaluation | null
}) {
  const [filter, setFilter] = React.useState('')
  const needle = filter.trim().toLowerCase()

  return (
    <Card>
      <CardHeader className="pb-3">
        <Input
          placeholder="Filter areas and criteria…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="pb-2 pr-3 font-medium">Area / criterion</th>
              {comparison && <th className="pb-2 px-3 text-right font-medium">Before</th>}
              <th className="pb-2 px-3 text-right font-medium">Score</th>
              <th className="pb-2 pl-3 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody>
            {stats.map(({ category, criteria, now, before }) => {
              const rows = criteria.filter(
                (c) => !needle || c.name.toLowerCase().includes(needle) || category.name.toLowerCase().includes(needle),
              )
              if (rows.length === 0) return null
              return (
                <React.Fragment key={category.id}>
                  <tr className="border-b bg-muted/40">
                    <th className="py-2 pr-3 text-left font-semibold">{category.name}</th>
                    {comparison && (
                      <td className="py-2 px-3 text-right tabular-nums text-muted-foreground">
                        {before != null ? before.toFixed(1) : '—'}
                      </td>
                    )}
                    <td className="py-2 px-3 text-right font-semibold tabular-nums">
                      {now != null ? now.toFixed(1) : '—'}
                    </td>
                    <td />
                  </tr>
                  {rows.map((c) => {
                    const s = scoreByCriterion.get(c.id)
                    const b = compareByCriterion.get(c.id)
                    return (
                      <tr key={c.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 pl-4 text-muted-foreground">{c.name}</td>
                        {comparison && (
                          <td className="py-1.5 px-3 text-right tabular-nums text-muted-foreground">
                            {b ?? '—'}
                          </td>
                        )}
                        <td className="py-1.5 px-3 text-right tabular-nums">
                          {!s
                            ? '—'
                            : // A yes/no reads as its own word and a percentage
                              // as a percentage. "10 / 10" is the storage, not
                              // the answer. Only a maturity scale, whose labels
                              // are words, falls through to the fraction.
                              (answerLabel(template, c, Number(s.score)) && (c.choices || unitLabel(template, Number(s.score)))
                                ? answerLabel(template, c, Number(s.score))
                                : `${Number(s.score)} / ${template.scale_max}`)}
                        </td>
                        <td className="py-1.5 pl-3 text-xs text-muted-foreground">{s?.notes}</td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
